import { useNavigate, useLocation } from "react-router-dom";

/**
 * BackButton — floating pill that stays anchored to the viewport (top-left,
 * below the header) rather than sitting in the content flow.
 *
 * Design intent:
 *   - Navy pill + gold text = brand-forward, high contrast on ANY page
 *     background (light hero, dark navy hero, gold section, etc.).
 *   - Fixed position means it never scrolls out of reach and doesn't fight
 *     the page hero for the eye's first stop.
 *   - Uses U+27F5 (⟵) for a longer, more inviting arrow than plain ←.
 *
 * Behavior: navigate(-1) when history exists; otherwise fall back to the
 * caller-supplied path or the branch home.
 */
export default function BackButton({ branch = "main", fallback }) {
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    if (window.history.length > 1 && location.key !== "default") {
      navigate(-1);
      return;
    }
    const branchHome = { hr: "/hr", continuum: "/continuum", main: "/" }[branch] || "/";
    navigate(fallback || branchHome);
  };

  return (
    <button
      type="button"
      onClick={goBack}
      className="fixed z-30 top-[148px] left-4 sm:left-6 inline-flex items-center gap-2 px-4 py-2 font-bold text-sm tracking-wide bg-[var(--navy)] text-[var(--gold)] border border-[var(--navy)] shadow-lg hover:bg-[var(--gold)] hover:text-[var(--navy)] hover:border-[var(--gold)] transition-colors"
      data-testid="back-button"
      aria-label="Go back"
    >
      <span aria-hidden="true" className="text-lg leading-none translate-y-[-1px]">⟵</span>
      Back
    </button>
  );
}
